import React, { useState, useMemo } from 'react';
import type { OrderItem, LoyaltyReward, OrderType } from '../../types';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import XCircleIcon from '../icons/XCircleIcon';
import MenuItemImage from '../menu/MenuItemImage';

interface CustomerConfirmationDisplayProps {
  orderItems: OrderItem[];
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  appliedReward: LoyaltyReward | null;
  notes?: string;
  orderType: OrderType;
  /** Manual delivery fee (GST incl.) for `delivery` orders. */
  deliveryCharge?: number;
  onConfirm: () => void;
  onCancel: () => void;
  isPlacingOrder?: boolean;
}

const CustomerConfirmationDisplay: React.FC<CustomerConfirmationDisplayProps> = ({
  orderItems,
  subtotal,
  taxAmount,
  totalAmount,
  appliedReward,
  notes,
  orderType,
  deliveryCharge = 0,
  onConfirm,
  onCancel,
  isPlacingOrder = false,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const discount = appliedReward ? appliedReward.discountValue : 0;
  const displayItems = useMemo(
    () => orderItems.filter((i) => i.removed !== true),
    [orderItems]
  );

  const handleConfirm = () => {
    setIsConfirming(true);
    setTimeout(() => {
      onConfirm();
      setIsConfirming(false);
    }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center p-4">
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl w-full max-w-6xl h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-6 py-3 flex-shrink-0">
          <h1 className="text-2xl font-bold">Order Summary</h1>
          <p className="text-pink-100 text-sm">
            {orderType === 'dine-in' 
              ? 'Please verify your order before sending to kitchen' 
              : orderType === 'uber-doordash'
              ? 'Confirm to send to kitchen — no payment needed (collected by Uber).'
              : 'Please verify your order before payment'}
          </p>
        </div>

        {/* Content - Split into two columns */}
        <div className="flex-1 flex gap-4 p-4 overflow-hidden">
          {/* LEFT COLUMN: Order Items (scrollable) */}
          <div className="flex-1 flex flex-col bg-white rounded-xl p-4 shadow-sm border border-gray-100 overflow-hidden">
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center flex-shrink-0">
              <span className="text-xl mr-2">📦</span>
              Your Items ({displayItems.length})
            </h2>
            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {displayItems.length > 0 ? (
                displayItems.map((item, index) => (
                  <div
                    key={`order-item-${index}-${item.id}`}
                    className="flex flex-col gap-2 p-3 bg-gradient-to-r from-gray-50 to-transparent rounded-lg hover:from-pink-50 transition-colors border border-gray-200"
                  >
                    <div className="flex items-center gap-2">
                      <MenuItemImage
                        src={item.imageUrlThumb || item.imageUrl || `https://picsum.photos/seed/${item.id}/100`}
                        alt={item.name}
                        className="w-12 h-12 rounded-lg flex-shrink-0 shadow-sm"
                        width={48}
                        height={48}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">${item.price.toFixed(2)} each</p>
                      </div>
                      <div className="bg-pink-100 text-pink-600 font-bold px-2 py-1 rounded text-sm flex-shrink-0">
                        x{item.quantity}
                      </div>
                      <div className="text-right flex-shrink-0 min-w-[60px]">
                        <p className="text-sm font-bold text-gray-800">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    {/* Display option if selected */}
                    {item.selectedMeatOption && (
                      <div className="ml-14">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-pink-100 text-pink-700 border border-pink-300">
                          Option: {item.selectedMeatOption}
                        </span>
                      </div>
                    )}
                    {/* Display item notes if present - highlighted in noticeable color */}
                    {item.notes && (
                      <div className="ml-14">
                        <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-3 py-2 shadow-sm">
                          <p className="text-xs font-bold text-yellow-900 mb-1 flex items-center">
                            <span className="mr-1">📝</span>
                            Special Instructions:
                          </p>
                          <p className="text-sm font-medium text-yellow-900">{item.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-8">No items in order</p>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Summary & Actions */}
          <div className="w-96 flex flex-col gap-3">
            {/* Special Instructions */}
            {notes && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex-shrink-0">
                <h3 className="font-bold text-gray-800 text-sm mb-1 flex items-center">
                  <span className="mr-1">📝</span>
                  Special Instructions
                </h3>
                <p className="text-gray-700 text-xs italic">{notes}</p>
              </div>
            )}

            {/* Price Breakdown */}
            <div className="flex-1 bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col">
              <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center flex-shrink-0">
                <span className="mr-2">💰</span>
                Payment Details
              </h2>
              <div className="space-y-2 flex-1">
                <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                  <span className="text-gray-700 text-sm">Subtotal (ex. GST)</span>
                  <span className="text-gray-800 font-bold">${subtotal.toFixed(2)}</span>
                </div>

                {appliedReward && (
                  <div className="flex justify-between items-center pb-2 border-b border-green-200 bg-green-50 px-2 py-1 rounded">
                    <span className="text-green-700 text-sm flex items-center">
                      <span className="mr-1">🎁</span>
                      {appliedReward.name}
                    </span>
                    <span className="text-green-600 font-bold">-${discount.toFixed(2)}</span>
                  </div>
                )}

                {orderType === 'delivery' && deliveryCharge > 0 && (
                  <div className="flex justify-between items-center pb-2 border-b border-pink-200 bg-pink-50 px-2 py-1 rounded">
                    <span className="text-pink-800 text-sm font-medium">Delivery charge (incl. GST)</span>
                    <span className="text-pink-700 font-bold">+${deliveryCharge.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                  <span className="text-gray-700 text-sm">GST (15%)</span>
                  <span className="text-gray-800 font-bold">${taxAmount.toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center pt-2 bg-gradient-to-r from-pink-50 to-rose-50 px-3 py-3 rounded-xl mt-auto">
                  <span className="text-lg font-bold text-gray-800">Total</span>
                  <span className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
                    ${totalAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={onCancel}
                disabled={isConfirming}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 flex items-center justify-center text-sm"
              >
                <XCircleIcon className="w-4 h-4 mr-1" />
                Cancel
              </button>
            <button
              onClick={handleConfirm}
              disabled={isConfirming || displayItems.length === 0 || isPlacingOrder}
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm shadow-lg hover:shadow-xl"
              >
                <CheckCircleIcon className="w-4 h-4 mr-1" />
                {isConfirming 
                  ? 'Confirming...' 
                  : orderType === 'dine-in' 
                    ? 'Confirm' 
                    : 'Confirm & Pay'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerConfirmationDisplay;

